import type {
  CreateSupportAccessGrantRequest,
  OperatorIdentity,
  SupportAccessGrantReadModel,
  SupportAccessMutationResult,
  SupportAccessWorkspace,
} from "@billiard/contracts";
import { Clock3, Copy, RotateCcw, ShieldCheck, ShieldX } from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import {
  ApiRequestError,
  createSupportAccessGrant,
  generateSupportAccessToken,
  getSupportAccessWorkspace,
  recoverSupportAccessMutation,
  revokeSupportAccessGrant,
  supportAccessOutcomeIsUnknown,
} from "./api";

const durationLabels: Record<CreateSupportAccessGrantRequest["durationMinutes"], string> = {
  15: "15 分钟（快速核对）",
  30: "30 分钟（常规排查）",
  60: "1 小时（较复杂排查）",
  120: "2 小时（最长单次授权）",
};

function statusLabel(status: SupportAccessGrantReadModel["status"]): string {
  return status === "ACTIVE" ? "当前有效" : status === "REVOKED" ? "老板已撤销" : "已经过期";
}

function localTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

type PendingOperation =
  | { type: "CREATE"; command: CreateSupportAccessGrantRequest }
  | {
      type: "REVOKE";
      idempotencyKey: string;
      grantId: string;
    };

export function SupportAccessPanel({
  operator,
}: {
  operator: OperatorIdentity;
}): React.JSX.Element | null {
  const titleId = useId();
  const [workspace, setWorkspace] = useState<SupportAccessWorkspace | null>(null);
  const [supportDisplayName, setSupportDisplayName] = useState("");
  const [reason, setReason] = useState("");
  const [durationMinutes, setDurationMinutes] =
    useState<CreateSupportAccessGrantRequest["durationMinutes"]>(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [lastToken, setLastToken] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingOperation | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setWorkspace(await getSupportAccessWorkspace());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "技术支持授权列表暂时无法读取");
    }
  }, []);

  useEffect(() => {
    if (operator.role === "OWNER") void refresh();
  }, [operator.role, refresh]);

  if (operator.role !== "OWNER") return null;

  const finishMutation = async (
    result: SupportAccessMutationResult,
    token?: string,
  ): Promise<void> => {
    setPending(null);
    setLastToken(token ?? null);
    setNotice(
      result.operation === "CREATE"
        ? "授权已经生效。请只把下面的一次性授权码交给本次技术支持人员。"
        : "授权已经撤销，技术支持人员再次刷新时会立即失去访问权限。",
    );
    await refresh();
  };

  const submit = async (): Promise<void> => {
    const name = supportDisplayName.trim();
    const purpose = reason.trim();
    if (!name || purpose.length < 2) {
      setError("请填写技术支持人员姓名和本次排查原因。原因至少写 2 个字。");
      return;
    }
    const command: CreateSupportAccessGrantRequest = {
      supportDisplayName: name,
      reason: purpose,
      durationMinutes,
      accessToken: generateSupportAccessToken(),
      idempotencyKey: `support-create-${crypto.randomUUID()}`,
    };
    setBusy(true);
    setError(null);
    setNotice(null);
    setLastToken(null);
    setPending({ type: "CREATE", command });
    try {
      await finishMutation(await createSupportAccessGrant(command), command.accessToken);
      setSupportDisplayName("");
      setReason("");
    } catch (caught) {
      setError(
        supportAccessOutcomeIsUnknown(caught)
          ? "服务端可能已经收到本次授权。页面保留了原请求，请点“核对原结果”，不要重新创建。"
          : caught instanceof Error
            ? caught.message
            : "技术支持授权没有创建",
      );
      if (!supportAccessOutcomeIsUnknown(caught)) setPending(null);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (grant: SupportAccessGrantReadModel): Promise<void> => {
    if (
      !window.confirm(`确认立即撤销“${grant.supportDisplayName}”的技术支持授权？撤销后不能恢复。`)
    )
      return;
    const command = {
      expectedVersion: grant.version,
      reason: "老板从授权列表手动撤销",
      idempotencyKey: `support-revoke-${crypto.randomUUID()}`,
    };
    setBusy(true);
    setError(null);
    setLastToken(null);
    setPending({ type: "REVOKE", idempotencyKey: command.idempotencyKey, grantId: grant.grantId });
    try {
      await finishMutation(await revokeSupportAccessGrant(grant.grantId, command));
    } catch (caught) {
      setError(
        supportAccessOutcomeIsUnknown(caught)
          ? "撤销结果暂时无法确认。页面保留了原请求，请点“核对原结果”，不要重复撤销。"
          : caught instanceof Error
            ? caught.message
            : "授权没有撤销",
      );
      if (!supportAccessOutcomeIsUnknown(caught)) setPending(null);
    } finally {
      setBusy(false);
    }
  };

  const recover = async (): Promise<void> => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      const result = await recoverSupportAccessMutation(
        pending.type,
        pending.type === "CREATE" ? pending.command.idempotencyKey : pending.idempotencyKey,
      );
      await finishMutation(
        result,
        pending.type === "CREATE" ? pending.command.accessToken : undefined,
      );
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError && caught.code === "SUPPORT_ACCESS_RESULT_NOT_FOUND"
          ? "服务端确认没有这次操作记录，可以重新发起。"
          : caught instanceof Error
            ? caught.message
            : "原授权结果仍无法确认",
      );
      if (caught instanceof ApiRequestError && caught.code === "SUPPORT_ACCESS_RESULT_NOT_FOUND") {
        setPending(null);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="support-access-panel" aria-labelledby={titleId}>
      <div className="section-heading">
        <div>
          <h2 id={titleId}>技术支持临时授权</h2>
          <p>
            只允许读取本店脱敏诊断；不能看顾客资料、金额明细、原始日志，也不能修改任何经营数据。
          </p>
        </div>
        <ShieldCheck size={22} aria-hidden="true" />
      </div>

      <div className="support-access-form">
        <label>
          <span>技术支持人员</span>
          <input
            value={supportDisplayName}
            maxLength={40}
            placeholder="例如：项目维护小王"
            onChange={(event) => setSupportDisplayName(event.target.value)}
          />
          <small>写清本次拿到授权码的人，方便老板事后核对。</small>
        </label>
        <label>
          <span>有效时间</span>
          <select
            value={durationMinutes}
            onChange={(event) =>
              setDurationMinutes(
                Number(event.target.value) as CreateSupportAccessGrantRequest["durationMinutes"],
              )
            }
          >
            {Object.entries(durationLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <small>到期后服务端自动拒绝，不需要老板守在电脑前关闭。</small>
        </label>
        <label className="support-access-reason">
          <span>本次排查原因</span>
          <textarea
            value={reason}
            maxLength={200}
            placeholder="例如：排查门店同步状态异常"
            onChange={(event) => setReason(event.target.value)}
          />
          <small>授权记录会保留原因；授权不会扩大到其他门店或其他能力。</small>
        </label>
        <button
          type="button"
          className="primary-button"
          disabled={busy || pending !== null}
          onClick={() => void submit()}
        >
          创建限时授权码
        </button>
      </div>

      {error ? (
        <div className="error-band" role="alert">
          {error}
        </div>
      ) : null}
      {pending ? (
        <button
          type="button"
          className="secondary-button"
          disabled={busy}
          onClick={() => void recover()}
        >
          <RotateCcw size={16} aria-hidden="true" /> 核对原结果
        </button>
      ) : null}
      {notice ? <output className="success-band">{notice}</output> : null}
      {lastToken ? (
        <output className="support-access-token">
          <strong>一次性授权码（关闭页面后不再显示）</strong>
          <code>{lastToken}</code>
          <button type="button" onClick={() => void navigator.clipboard.writeText(lastToken)}>
            <Copy size={16} aria-hidden="true" /> 复制授权码
          </button>
        </output>
      ) : null}

      <div className="support-access-list">
        {(workspace?.grants ?? []).length === 0 ? (
          <p className="empty-state">本店还没有技术支持授权。</p>
        ) : (
          (workspace?.grants ?? []).map((grant) => (
            <article
              key={grant.grantId}
              className={`support-access-card is-${grant.status.toLowerCase()}`}
            >
              <div>
                <strong>{grant.supportDisplayName}</strong>
                <span>{statusLabel(grant.status)}</span>
              </div>
              <p>{grant.reason}</p>
              <small>
                <Clock3 size={14} aria-hidden="true" /> {localTime(grant.validFrom)} 至{" "}
                {localTime(grant.validUntil)} · 仅脱敏诊断
              </small>
              {grant.status === "ACTIVE" ? (
                <button type="button" disabled={busy} onClick={() => void revoke(grant)}>
                  <ShieldX size={16} aria-hidden="true" /> 立即撤销
                </button>
              ) : null}
            </article>
          ))
        )}
      </div>
    </section>
  );
}
